import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { fmt, getQuoteNo } from '../utils';
import { Total } from './Summary';
import { useCountdown, clockText } from '../hooks/useCountdown';
import NextSteps from './NextSteps';

gsap.registerPlugin(ScrollTrigger);

const pad = (n) => String(n).padStart(2, '0');

const Row = ({ label, sub, value, market }) => {
  const el = useRef();
  useLayoutEffect(() => {
    gsap.fromTo(
      el.current,
      { x: -14, opacity: 0, backgroundColor: 'rgba(15,94,255,0.10)' },
      { x: 0, opacity: 1, backgroundColor: 'rgba(15,94,255,0)', duration: 0.7, ease: 'power3.out' },
    );
  }, []);
  return (
    <div ref={el} className="-mx-2 flex items-start justify-between gap-4 rounded-lg px-2 py-2">
      <span className="text-[13px] text-ink">
        {label}
        {sub && <span className="block text-[11px] text-gray">{sub}</span>}
      </span>
      <span className="flex items-baseline gap-1.5 whitespace-nowrap">
        {market && <span className="tabular text-[11px] text-gray line-through">{fmt(market)}</span>}
        {value === 0 ? (
          <span className="tabular text-[13px] font-medium text-[#1d9c5e]">Free</span>
        ) : (
          <span className="tabular text-[13px] font-medium text-ink">{fmt(value)}</span>
        )}
      </span>
    </div>
  );
};

const Receipt = ({ build, className = '' }) => {
  const countdown = useCountdown();
  const slot = useRef();
  const paper = useRef();
  const totalRef = useRef();
  const barcode = useRef();
  const stamp = useRef();
  const [stamped, setStamped] = useState(false);
  const quoteNo = useMemo(() => getQuoteNo(), []);
  const [now, setNow] = useState(new Date());
  const [updated, setUpdated] = useState(false);
  const first = useRef(true);

  // Print out of the slot when it scrolls into view
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(paper.current, {
        yPercent: -100,
        duration: 1.2,
        ease: 'power3.out',
        scrollTrigger: { trigger: slot.current, start: 'top 85%', once: true },
      });
    });
    return () => ctx.revert();
  }, []);

  // Re-print effects on every change
  useEffect(() => {
    setNow(new Date());
    if (first.current) { first.current = false; return; }
    gsap.fromTo(paper.current, { y: -6 }, { y: 0, duration: 0.6, ease: 'elastic.out(1, 0.45)' });
    gsap.fromTo(totalRef.current, { scale: 1.12 }, { scale: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)', transformOrigin: 'right center' });
    gsap.fromTo(barcode.current, { width: '0%' }, { width: '100%', duration: 0.55, ease: 'power1.inOut' });
    setUpdated(true);
    const t = setTimeout(() => setUpdated(false), 1100);
    return () => clearTimeout(t);
  }, [build.total]);

  const onOrder = (e) => {
    if (stamped) return;
    setStamped(true);
    gsap.fromTo(
      stamp.current,
      { scale: 3, opacity: 0, rotate: -18 },
      { scale: 1, opacity: 1, rotate: -12, duration: 0.45, ease: 'back.out(2)' },
    );
    gsap.fromTo(paper.current, { y: 0 }, { y: 4, duration: 0.08, yoyo: true, repeat: 1, delay: 0.3 });
    setTimeout(() => {
      gsap.to(stamp.current, { opacity: 0, duration: 0.6 });
      setStamped(false);
    }, 2400);
  };

  const d = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
  const t = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

  return (
    <div className={className}>
      {/* printer slot */}
      <div className="mx-4 h-2 rounded-full bg-[#E2E8F0]" />
      <div ref={slot} className="relative -mt-1 overflow-hidden px-1 pb-3 pt-1">
        <div
          ref={paper}
          className="receipt shimmer relative overflow-hidden rounded-t-2xl bg-white px-6 pb-5 pt-6 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.25)]"
        >
          <div
            ref={stamp}
            className="stamp pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 opacity-0"
          >
            SENT
          </div>
          <div className="flex items-start justify-between">
            <div>
              <p className="flex items-center gap-2 text-[15px] font-semibold text-ink">
                Order summary
                <span
                  className={`rounded-full bg-blue px-2 py-0.5 text-[10px] font-semibold text-white transition-all duration-300 ${
                    updated ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                  }`}
                >
                  Updated
                </span>
              </p>
              <p className="text-[11px] text-gray">Ref {quoteNo}</p>
            </div>
            <div className="text-right text-[11px] text-gray">
              <p>{d}</p>
              <p>{t}</p>
            </div>
          </div>

          <div className="my-4 border-t border-[#E2E8F0]" />

          <div className="divide-y divide-[#F1F5FB]">
            {build.lines.map((l) => (
              <Row key={l.name} label={l.name} sub={l.sub} value={l.price} market={l.market} />
            ))}
          </div>

          {build.discounts.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-gray">Subtotal</span>
              <span className="tabular text-ink">₹{fmt(build.subtotal)}</span>
            </div>
            {build.discounts && build.discounts.map((d) => (
              <div key={d.label} className="flex items-start justify-between text-[13px]">
                <span className="font-medium text-[#1d9c5e]">
                  {d.label}
                  <span className="block font-normal text-[11px] text-gray">{d.sub}</span>
                </span>
                <span className="tabular shrink-0 whitespace-nowrap font-medium text-[#1d9c5e]">-₹{fmt(d.amount)}</span>
              </div>
            ))}
          </div>
          )}

          <div className="my-4 border-t border-[#E2E8F0]" />

          <div className="flex items-center justify-between text-[12px]">
            <span className="text-gray">Market value</span>
            <span className="tabular text-gray line-through">₹{fmt(build.marketTotal)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-[12px]">
            <span className="font-medium text-[#1d9c5e]">You save</span>
            <span className="tabular font-medium text-[#1d9c5e]">₹{fmt(build.savings)}</span>
          </div>
          <div className="mt-3 flex items-end justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray">Total</span>
            <span ref={totalRef} className="inline-block">
              <Total build={build} size="text-3xl text-ink" />
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="whitespace-nowrap rounded-full bg-[#F1F5FB] px-2.5 py-1 text-[11px] font-medium text-ink">Live in 24 hours*</span>
            <span className="whitespace-nowrap rounded-full bg-[#F1F5FB] px-2.5 py-1 text-[11px] font-medium text-ink">Secure payment via Razorpay</span>
          </div>
          <p className="mt-2 text-[11px] text-gray">UPI, cards and netbanking accepted. *From when we receive your details.</p>

          <div className="mt-5 flex items-center justify-between border-t border-dashed border-[#E2E8F0] pt-4">
            <span className="text-[11px] text-gray">{build.lines.length} item{build.lines.length > 1 ? 's' : ''} · one-time</span>
            <span className="relative block h-5 w-24">
              <span ref={barcode} className="barcode absolute inset-y-0 left-0 w-full opacity-70" />
            </span>
          </div>
        </div>
        <div className="receipt-edge" />
      </div>

      {countdown.active && build.offerDiscount > 0 ? (
        <div className="mt-3 flex items-center justify-between rounded-2xl bg-ink px-4 py-2.5 text-white">
          <span className="text-[12px] font-medium text-white/70">Offer ends in</span>
          <span className="tabular text-lg font-semibold">{clockText(countdown)}</span>
        </div>
      ) : countdown.active && build.offerDiscount === 0 && build.platform !== 'wp' ? (
        <button
          type="button"
          onClick={() => build.setPlatform('wp')}
          className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-2xl bg-ink px-4 py-2.5 text-white hover:bg-black"
        >
          <span className="text-[12px] font-medium text-white/70">Free WordPress upgrade ends in</span>
          <span className="tabular text-lg font-semibold">{clockText(countdown)}</span>
        </button>
      ) : null}

      <a
        href={build.waHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onOrder}
        className="btn-wa mt-3 w-full !py-3.5 !text-base"
      >
        {stamped ? 'Opening WhatsApp…' : 'Order on WhatsApp'}
      </a>
      <NextSteps className="mt-4 px-1" />
    </div>
  );
};

export default Receipt;
